import { useState, useEffect } from 'react';
import { getDocs, getDoc } from 'firebase/firestore';

/**
 * Custom hook for fetching data from Firestore
 * 
 * @param {Object} options - Query options
 * @param {Function} options.queryFn - Function that returns a Firestore query or reference
 * @param {Array} [options.dependencies=[]] - Dependencies array for useEffect
 * @param {Function} [options.transformFn] - Optional function to transform the data
 * @returns {Object} - { data, loading, error }
 * 
 * @example
 * // Fetch a collection
 * const { data: blogs, loading, error } = useFirestoreQuery({
 *   queryFn: () => query(collection(db, 'blogs'), where('isPublished', '==', true)),
 *   dependencies: [],
 *   transformFn: (docs) => docs.map(doc => ({ id: doc.id, ...doc.data() }))
 * });
 * 
 * @example
 * // Fetch a single document
 * const { data: blog, loading, error } = useFirestoreQuery({
 *   queryFn: () => doc(db, 'blogs', blogId),
 *   dependencies: [blogId],
 *   transformFn: (docSnap) => docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null
 * });
 */
const useFirestoreQuery = ({ queryFn, dependencies = [], transformFn }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      try {
        const query = queryFn();
        
        // Handle both collection queries and document references
        let result;
        if (query.type === 'document') {
          // It's a document reference
          const docSnap = await getDoc(query);
          result = transformFn ? transformFn(docSnap) : docSnap;
        } else {
          // It's a collection query
          const querySnapshot = await getDocs(query);
          result = transformFn ? transformFn(querySnapshot) : querySnapshot;
        }
        
        setData(result);
      } catch (err) {
        console.error('Error fetching from Firestore:', err);
        setError('Error fetching data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return { data, loading, error };
};

export default useFirestoreQuery;